import React, { useCallback, useEffect, useState } from 'react';
import List from './List';
import { LegacyCustomer } from 'src/interfaces/api/Customer';
import { fetchCustomers } from './List.utils';
import { Order } from 'src/interfaces/Table';
import { CustomersFilter } from './components/FiltersButton/components/FiltersDialog';
import 'react-leaflet-markercluster/dist/styles.min.css';
import { debounce } from 'lodash';
import useAuth from '../../../../hooks/api/useAuth';
import useAgencies from '../../../../hooks/api/useAgencies';

const ListContainer = () => {
  const [customers, setCustomers] = useState<LegacyCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState<keyof LegacyCustomer>('dateCreate');
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState<Order>('desc');
  const { fetch, token } = useAuth();
  const { agencies } = useAgencies();

  const [filters, setFilters] = useState<Partial<Record<CustomersFilter, any>>>({
    agencies: token?.agenciesIds || [],
    employees: [],
    displayProspect: true,
    displayLost: true,
    displayWin: true,
  });

  useEffect(() => {
    setLoading(true);

    fetchCustomers(fetch, orderBy, order)
      .then((customers) => {
        setCustomers(customers.items);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [orderBy, order]);

  const handleQueryChange = debounce((query: string) => setQuery(query), 600);

  const handleFilterChange = (newFilters: Partial<Record<CustomersFilter, any>>) => {
    setFilters(newFilters);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof LegacyCustomer) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getFilteredCustomers = useCallback(() => {
    let filteredCustomers = customers.filter((customer) => {
      const socialReason = customer.installationSite?.socialReason || '';
      return socialReason.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });

    if ('agencies' in filters && agencies.status === 'fetched') {
      const legacyIds = agencies.data
        .filter((agency) => filters.agencies.includes(agency.id))
        .map((agency) => agency.legacyId);
      filteredCustomers = filteredCustomers.filter((customer) => legacyIds.includes(customer.agency?.id));
    }

    if ('employees' in filters && filters.employees.length > 0) {
      filteredCustomers = filteredCustomers.filter(
        (customer) =>
          filters.employees.includes(customer.assignedEmployeeInformatique?.id) ||
          filters.employees.includes(customer.assignedEmployeeTelecom?.id)
      );
    }

    if ('displayLost' in filters && !filters.displayLost) {
      filteredCustomers = filteredCustomers.filter((customer) => customer.state !== 'lost');
    }

    if ('displayWin' in filters && !filters.displayWin) {
      filteredCustomers = filteredCustomers.filter((customer) => customer.state !== 'customer');
    }

    if ('displayProspect' in filters && !filters.displayProspect) {
      filteredCustomers = filteredCustomers.filter((customer) => customer.state !== 'prospect');
    }

    return filteredCustomers;
  }, [filters, customers, query]);

  return (
    <List
      filters={filters}
      loading={loading}
      onFilterChange={handleFilterChange}
      customers={getFilteredCustomers()}
      onQueryChange={handleQueryChange}
      order={order}
      orderBy={orderBy}
      onRequestSort={handleRequestSort}
    />
  );
};

export default ListContainer;
